import { useCallback, useEffect, useState } from 'react';

import { api } from '../lib/api.js';

type Status = 'unsupported' | 'denied' | 'granted-not-subscribed' | 'subscribed' | 'unknown';

interface UsePushReturn {
  status: Status;
  loading: boolean;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  test: () => Promise<{ sent: number; failed: number; totalSubscriptions: number } | null>;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function usePushNotifications(): UsePushReturn {
  const [status, setStatus] = useState<Status>('unknown');
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setStatus('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setStatus('denied');
      return;
    }
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = await reg?.pushManager.getSubscription();
    if (sub) setStatus('subscribed');
    else if (Notification.permission === 'granted') setStatus('granted-not-subscribed');
    else setStatus('granted-not-subscribed');
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const subscribe = useCallback(async () => {
    setLoading(true);
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error('Navegador não suporta Web Push.');
      }
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      await navigator.serviceWorker.ready;

      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setStatus('denied');
        return;
      }

      const { data } = await api.get<{ available: boolean; publicKey: string | null }>(
        '/push/vapid-public-key',
      );
      if (!data.available || !data.publicKey) {
        throw new Error('Servidor sem VAPID configurado.');
      }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        // Cast: o DOM lib espera BufferSource — Uint8Array.buffer é ArrayBuffer.
        applicationServerKey: urlBase64ToUint8Array(data.publicKey).buffer as ArrayBuffer,
      });
      const json = sub.toJSON();
      await api.post('/push/subscribe', {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
      });
      setStatus('subscribed');
    } finally {
      setLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await api.delete('/push/unsubscribe', { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setStatus('granted-not-subscribed');
    } finally {
      setLoading(false);
    }
  }, []);

  const test = useCallback(async () => {
    const { data } = await api.post<{
      ok: boolean;
      sent: number;
      failed: number;
      totalSubscriptions: number;
    }>('/push/test', {});
    if (!data.ok) return null;
    return data;
  }, []);

  return { status, loading, subscribe, unsubscribe, test };
}
